import React, { useEffect, useState, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import {
  deleteLanguage,
  getAllLanguages,
  postLanguage,
  updateLanguage,
  languageSequence,
} from "../../../../../libs/api/languageApi";
import Pagination from "../../../../../libs/Pagination";
import { AppContext } from "../../../../../context/appContext";

export default function LanguageMaster() {
  const { t, formatNum, isRTL } = useContext(AppContext);
  const [languagesArr, setLanguagesArr] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddLangLoading, setIsAddLangLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [modalMode, setModalMode] = useState("add");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "" });
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [errors, setErrors] = useState({ name: "" });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const openModal = (mode, idx = null) => {
    setModalMode(mode);
    setSelectedIdx(idx);
    if (mode === "edit" && idx !== null) {
      setForm({
        name: languagesArr[idx].name,
      });
    } else {
      setForm({ name: "" });
    }
    setErrors({ name: "" });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm({ name: "" });
    setSelectedIdx(null);
    setErrors({ name: "" });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t('validation.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, currentPage: newPage });
    }
  };

  // api call
  const postLanguageFunc = async (payload) => {
    setIsAddLangLoading(true);
    try {
      const res = await postLanguage(payload);
      if (res?.data?.success) {
        toast.success(res?.data?.message || t('messages.createdSuccess'));
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsAddLangLoading(false);
    }
  };

  const getLanguageFunc = async (search, pagination) => {
    if (languagesArr.length === 0 || !languagesArr) {
      setIsLoading(true);
    }
    try {
      const res = await getAllLanguages(search, pagination);
      if (res?.data?.success) {
        setLanguagesArr(res?.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: res?.data?.pagination?.totalPages || 0,
          totalRecords: res?.data?.pagination?.total || 0,
        }));
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const updateLanguageFunc = async (payload, selectedLangId) => {
    setIsAddLangLoading(true);
    try {
      const res = await updateLanguage(payload, selectedLangId);
      if (res?.data?.success) {
        toast.success(res?.data?.message || t('messages.updatedSuccess'));
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsAddLangLoading(false);
    }
  };

  const deleteLanguageFunc = async (langId) => {
    try {
      const res = await deleteLanguage(langId);
      if (res?.data?.success) {
        toast.success(res?.data?.message || t('messages.deletedSuccess'));
        await getLanguageFunc(debouncedSearch, pagination);
      } else {
        toast.error(res?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const newLang = {
      name: form.name,
    };

    if (modalMode === "add") {
      setLanguagesArr([...languagesArr, newLang]);
      await postLanguageFunc(form);
    } else if (modalMode === "edit" && selectedIdx !== null) {
      const updated = [...languagesArr];
      updated[selectedIdx] = newLang;
      const selectedLangId = languagesArr[selectedIdx]?._id;
      setLanguagesArr(updated);
      await updateLanguageFunc(form, selectedLangId);
    }
    closeModal();
    await getLanguageFunc(debouncedSearch, pagination);
  };

  const handleDelete = async (idx, langId) => {
    setLanguagesArr(languagesArr.filter((_, i) => i !== idx));
    await deleteLanguageFunc(langId);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(languagesArr[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    draggedElement.style.opacity = '0.3';

    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(languagesArr[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = languagesArr[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence || 0;
    const targetSequence = draggedOverItem.sequence || 0;

    const updatedLanguages = languagesArr.map(language => {
      if (language._id === draggedItem._id) {
        return { ...language, sequence: targetSequence };
      }
      if (language._id === draggedOverItem._id) {
        return { ...language, sequence: draggedSequence };
      }
      return language;
    });

    // Sort by sequence to reflect the swap
    updatedLanguages.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    setLanguagesArr(updatedLanguages);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedToUpdateSequence'));
      await getLanguageFunc(debouncedSearch, pagination);
    }
  };

  const handleDragEnd = (e) => {
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await languageSequence(payload);
      if (response.status === 200 || response.status === 201) {
        await getLanguageFunc(debouncedSearch, pagination);
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getLanguageFunc(debouncedSearch, pagination);
  }, [pagination.currentPage, debouncedSearch]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">{t('language.title')}</h2>
      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Language */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2 border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('language.addLanguage')}
          </button>
        </div>

        {/* Table */}
        {languagesArr.length === 0 && !isLoading ? (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('language.noLanguages')}</p>
          </div>
        ) : languagesArr.length === 0 && isLoading ? (
          <div className="flex justify-center items-center h-full p-6">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-4 border-b border-[#F7F7F9]">
              <div className="p-2 py-4 w-12"></div>
              <div className={`p-2 py-4 col-span-2 ${isRTL ? 'pr-4' : 'pl-4'}`}>{t('language.languageName')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>
            <div>
              {languagesArr.map((elem, idx) => (
                <div
                  key={elem?._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  className="grid grid-cols-4 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none cursor-move"
                >
                  <div className="p-2 py-6 flex items-center justify-center w-12">
                    <FaGripLines size={20} className="text-gray-400" />
                  </div>
                  <div className={`p-2 py-6 col-span-2 ${isRTL ? 'pr-4' : 'pl-4'} flex items-center capitalize`}>
                    {elem?.name}
                  </div>
                  <div className="p-2 flex items-center">
                    <div className="flex gap-4 items-center">
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", idx)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(idx, elem?._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && languagesArr.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((pagination.currentPage - 1) * pagination.limit + 1)} {t('pagination.to')}{" "}
              {formatNum(Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalRecords
              ))}{" "}
              {t('pagination.of')} {formatNum(pagination.totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl min-w-[400px] max-w-[95vw] w-full md:w-[400px] p-0 shadow-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-lg">
                  {modalMode === "add" ? t('language.addLanguage') : t('language.editLanguage')}
                </h3>
                <button
                  className="text-xl hover:text-red-500"
                  onClick={closeModal}
                >
                  <IoClose />
                </button>
              </div>
              {/* Language Name */}
              <label className="block text-sm font-medium mb-2">
                {t('language.languageName')}
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                placeholder={t('forms.enterName')}
                className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mb-2">{errors.name}</p>
              )}
            </div>
            <div className="modal-action rounded-b-2xl bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
              <button
                type="button"
                disabled={isAddLangLoading}
                onClick={handleSubmit}
                className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
              >
                {isAddLangLoading ? (
                  <LoadingSpinner />
                ) : modalMode === "add" ? (
                  t('common.add')
                ) : (
                  t('common.update')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
